'use client';

import { useMemo, useState } from 'react';
import type { WorkloadMetricsInterval } from '@/lib/urm-api';
import { MetricsLoadingOverlay } from '@/components/metrics-loading-overlay';
import { buildPerUserBreakdown, listApiTuples } from '../per-user.logic';

function formatLatency(value: number | null): string {
  if (value === null) return '—';
  return value >= 1_000
    ? `${(value / 1_000).toLocaleString(undefined, { maximumFractionDigits: 2 })} s`
    : `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ms`;
}

function formatPercent(value: number | null): string {
  if (value === null) return '—';
  if (value > 0 && value < 0.1) return '<0.1%';
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

export function PerUserBreakdown({
  intervals,
  loading,
}: {
  intervals: WorkloadMetricsInterval[];
  loading: boolean;
}) {
  const tuples = useMemo(() => listApiTuples(intervals), [intervals]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const activeKey = selectedKey && tuples.some((tuple) => tuple.key === selectedKey)
    ? selectedKey
    : tuples[0]?.key ?? null;
  const rows = useMemo(
    () => (activeKey ? buildPerUserBreakdown(intervals, activeKey) : []),
    [intervals, activeKey],
  );

  return (
    <section
      aria-label="Per-user breakdown"
      className="space-y-2 rounded-xl border border-primary/20 bg-muted/40 p-2.5 shadow-sm"
    >
      <div className="min-w-0 rounded-lg border bg-card px-3 pt-3 pb-2 shadow-sm">
        <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h3 className="text-sm font-semibold">Per-user breakdown</h3>
          <p className="text-xs text-muted-foreground">
            Pick one ResourceType · Action · APILabel and see each user&apos;s values for it.
          </p>
        </div>
        <MetricsLoadingOverlay loading={loading}>
          {tuples.length === 0 && !loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No workload metrics were recorded in this window.</div>
          ) : (
            <>
              <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                API
                <select
                  aria-label="API tuple"
                  value={activeKey ?? ''}
                  onChange={(event) => setSelectedKey(event.target.value)}
                  className="rounded-md border bg-background px-2 py-1 font-mono text-xs text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {tuples.map((tuple) => (
                    <option key={tuple.key} value={tuple.key}>{tuple.label}</option>
                  ))}
                </select>
              </label>
              <div className="max-h-[720px] overflow-auto">
                <table aria-label="Per-user breakdown details" className="w-full min-w-[760px] table-fixed border-separate border-spacing-0 text-xs">
                  <colgroup>
                    <col className="w-[22%]" />
                    <col className="w-[13%]" />
                    <col className="w-[13%]" />
                    <col className="w-[13%]" />
                    <col className="w-[13%]" />
                    <col className="w-[13%]" />
                    <col className="w-[13%]" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-left font-medium text-muted-foreground">User</th>
                      <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-right font-medium text-muted-foreground">Requests</th>
                      <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-right font-medium text-muted-foreground">Avg latency</th>
                      <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-right font-medium text-muted-foreground">p50</th>
                      <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-right font-medium text-muted-foreground">p95</th>
                      <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-right font-medium text-muted-foreground">Processing share</th>
                      <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-right font-medium text-muted-foreground">≥1 second</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.key}>
                        <td title={row.label} className="truncate border-b px-2 py-1.5 font-mono">{row.label}</td>
                        <td className="border-b px-2 py-1.5 text-right font-mono tabular-nums">{row.totalRequests.toLocaleString()}</td>
                        <td className="whitespace-nowrap border-b px-2 py-1.5 text-right font-mono tabular-nums">{formatLatency(row.averageDurationMs)}</td>
                        <td className="whitespace-nowrap border-b px-2 py-1.5 text-right font-mono tabular-nums">{formatLatency(row.histogramP50Ms)}</td>
                        <td className="whitespace-nowrap border-b px-2 py-1.5 text-right font-mono tabular-nums">{formatLatency(row.histogramP95Ms)}</td>
                        <td className="border-b px-2 py-1.5 text-right font-mono tabular-nums">{formatPercent(row.processingSharePercent)}</td>
                        <td className="border-b px-2 py-1.5 text-right font-mono tabular-nums">{formatPercent(row.slowerThan1000Percent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </MetricsLoadingOverlay>
      </div>
    </section>
  );
}
